import { useSyncExternalStore, type CSSProperties } from 'react';

type VisibleArea = { height: number; offsetTop: number };

/**
 * The part of the page you can see: shorter than the window while a phone's keyboard is up, and moved
 * down when the browser has panned to the field you're typing in. iOS Safari doesn't shrink `dvh` for
 * the keyboard, so a full-height pop-up's bottom stays behind it, out of reach of its own scrolling (T-35).
 * Null where the browser has no visualViewport, or while `enabled` is false (nothing to re-render for).
 */
export function useVisibleArea(enabled = true): VisibleArea | null {
  return useSyncExternalStore(enabled ? subscribe : subscribeNone, enabled ? getSnapshot : getNone, getNone);
}

const subscribeNone = () => () => {};
const getNone = () => null;

let last: VisibleArea | null = null;

function getSnapshot(): VisibleArea | null {
  const viewport = typeof window === 'undefined' ? null : window.visualViewport;
  if (!viewport) return null;
  const height = Math.round(viewport.height);
  const offsetTop = Math.round(viewport.offsetTop);
  // The same object while nothing changed, as useSyncExternalStore needs.
  if (last?.height !== height || last.offsetTop !== offsetTop) last = { height, offsetTop };
  return last;
}

function subscribe(onChange: () => void) {
  const viewport = window.visualViewport;
  if (!viewport) return () => {};
  viewport.addEventListener('resize', onChange);
  viewport.addEventListener('scroll', onChange);
  return () => {
    viewport.removeEventListener('resize', onChange);
    viewport.removeEventListener('scroll', onChange);
  };
}

/**
 * Phones: a pop-up or panel that fills the screen, kept to the part you can see, so its own scrolling reaches the
 * bottom with the keyboard up. Only follows the screen while `open`. Pair it with classes that put it
 * at the top (`max-sm:inset-0`, `top-0`).
 */
export function usePhoneFullScreenStyle(open: boolean): CSSProperties | undefined {
  const phone = useIsPhone();
  const area = useVisibleArea(open && phone);
  if (!phone || !area) return undefined;
  return { top: area.offsetTop, height: area.height, maxHeight: area.height };
}

/** Below Tailwind's `sm` (640px), where the `max-sm:` classes apply. */
export const PHONE_QUERY = '(max-width: 639px)';

export function useIsPhone(): boolean {
  return useSyncExternalStore(subscribePhone, () => window.matchMedia(PHONE_QUERY).matches, () => false);
}

function subscribePhone(onChange: () => void) {
  const query = window.matchMedia(PHONE_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}
